import { useEffect } from "react";

import Typography from "@/design-system/Typography";
import type { IButtonsBarV2 } from "@/design-system/buttons-bar";
import ButtonsBarV2 from "@/design-system/buttons-bar";
import type { ITelemetryButtonProps } from "@/design-system/telemetry-button";
import { isClient } from "@/lib/common";
import { useOutsideClick } from "@/lib/hooks/use-outside-click";
import { sendTelemetryEvent } from "@/lib/telemetry";
import { RulesModalResources } from "@/src/cashback/RuleModal.Resources";
import { RulesModalResourcesEn } from "@/src/cashback/RuleModal.Resources.En";
import AdTag from "@/src/common/AdTag";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { browserStatics } from "@/src/shared/BrowserStatic";
import ModalV2 from "@Components/ModalV2";
import { CashbackEventType, TelemetryEntity, sendToGTMDiscountDetails } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QueryXsMax, colors } from "../../styles/tokens.stylex";

export type IRulesModal = {
    id?: string;
    messages: Array<{
        iconUrl?: string;
        text: string;
    }>;
    showCloseButton?: boolean;
    className?: string;
    brandId?: string;
    discountId?: string;
    withoutModal?: boolean;
    onClose: () => void;
    showAd: boolean;
    trackingActive?: boolean;
    buttonsBar: IButtonsBarV2;
};

export const RulesModal = (props: IRulesModal) => {
    const [, , action, eventLabel] = isClient ? window.location.pathname.split("/") : [];

    const gtmBrand = props.brandId ? action : undefined;
    const gtmDiscount = props.discountId ? eventLabel : undefined;

    const resources = languageSelector(RulesModalResources, RulesModalResourcesEn);

    const { isMobile } = useViewportInfoContext();
    const { shouldUserSeeAds } = browserStatics;

    const onCancelClick = () => {
        sendToGTMDiscountDetails("common_back", gtmBrand, gtmDiscount);
        props.onClose();
    };

    const ref = useOutsideClick<HTMLDivElement>(onCancelClick);

    useEffect(() => {
        if (props.trackingActive) {
            sendTelemetryEvent({
                eventName: `${TelemetryEntity.Cashback}-${CashbackEventType.OutboundInfo}`,
                actionType: "View",
                itemId: props.brandId,
                param1: props.discountId || undefined,
                param2: props.discountId ? "1" : undefined,
            });
        }
    }, []);

    const newCancelButton: ITelemetryButtonProps = !props.withoutModal && {
        ...props.buttonsBar.cancelButton,
        onClick: (
            ev: React.MouseEvent<HTMLButtonElement, MouseEvent> &
                React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        ) => {
            if (props.buttonsBar?.cancelButton?.onClick) {
                props.buttonsBar?.cancelButton?.onClick(ev);
            }
            onCancelClick();
        },
    };

    const content = (
        <>
            {props.showAd && shouldUserSeeAds && (
                <AdTag
                    styleX={styles.adTag}
                    adUnitId={`/${process.env.AD_UNIT}/web_goodiepage_przejscie_rectangle`}
                    optId="cashback_rules_modal"
                    closeOnLoadFail={false}
                    viewportSizes={[
                        {
                            viewport: [0, 0],
                            slot: [300, 250],
                        },
                    ]}
                />
            )}
            <Typography textElement="h2" fontStyle="hXS" styleX={styles.title}>
                {resources.title}
            </Typography>
            <Typography textElement="p" fontStyle={isMobile ? "BodyM" : "hXS"} styleX={styles.shortText}>
                {resources.shortText}
            </Typography>
            <div {...stylex.props(styles.contentWrapper)}>
                {props.messages?.map(({ iconUrl, text }, index) => (
                    <div key={index} {...stylex.props(styles.iconText)}>
                        {iconUrl && <img {...stylex.props(styles.icon)} src={iconUrl} alt={text} />}
                        <Typography
                            textElement="span"
                            fontStyle={isMobile ? "BodyS" : "BodyM"}
                            styleX={styles.text}
                        >
                            {text}
                        </Typography>
                    </div>
                ))}
                <div {...stylex.props(styles.legal)}>
                    {resources.legalInfo.map((text, index) => (
                        <Typography
                            key={index}
                            textElement="p"
                            fontStyle={isMobile ? "BodyS" : "BodyM"}
                            styleX={styles.text}
                        >
                            {text}
                        </Typography>
                    ))}
                </div>
            </div>

            {props.buttonsBar && (
                <ButtonsBarV2
                    {...props.buttonsBar}
                    cancelButton={newCancelButton}
                    nextButtonText={props.buttonsBar.nextButtonText || resources.btnNext}
                    cancelButtonText={props.buttonsBar.cancelButtonText || resources.btnCancel}
                    styleX={[styles.internalButtonsBar, props.buttonsBar.styleX]}
                />
            )}
        </>
    );

    return props.withoutModal === true ? (
        <div id={props.id || "cashback-rules-modal"} {...stylex.props(styles.mainFullWidth)}>
            {content}
        </div>
    ) : (
        <ModalV2
            ref={ref}
            id={props.id || "cashback-rules-modal"}
            onClose={() => {
                props.onClose();
                sendToGTMDiscountDetails("common_exit", gtmBrand, gtmDiscount);
                sendTelemetryEvent({
                    eventName: `${TelemetryEntity.Common}-exit`,
                    actionType: "Click",
                });
            }}
            styleX={styles.modal}
            sectionStyleX={styles.mainWrapper}
            mainStyleX={styles.main}
            headerStyleX={styles.resetHeader}
            closeButtonStyleX={styles.closeButton}
        >
            {content}
        </ModalV2>
    );
};

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    modal: {
        overflow: { default: "auto", [xsMax]: "hidden" },
    },

    resetHeader: {
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },

    main: {
        gridTemplateAreas: {
            // eslint-disable-next-line quotes
            default: '"ignore title" "ignore shortText" "adTag content" "adTag buttonsBar"',
            // eslint-disable-next-line quotes
            [xsMax]: `"title" "adTag" "shortText" "content" "buttonsBar"`,
        },
        display: "grid",
        gridTemplateColumns: { default: "minmax(auto, 0) auto", [xsMax]: "auto" },
        gridTemplateRows: { default: "repeat(4,auto)", [xsMax]: "auto auto auto 1fr auto" },

        rowGap: { default: 35, [xsMax]: 20 },
        maxHeight: { default: "95vh", [xsMax]: "100%" },
        paddingBottom: 20,
        paddingTop: 20,
    },

    mainWrapper: {
        borderRadius: { default: 12, [xsMax]: 0 },
        borderStyle: "none",
        backgroundColor: colors.bg_BrandPrimary,
        maxHeight: { default: "95vh", [xsMax]: "100%" },
        width: "auto",
    },

    closeButton: {
        color: colors.fg_AltDefault,
    },

    contentWrapper: {
        gridArea: "content",
        gap: { default: 35, [xsMax]: 20 },
        display: "flex",
        flexDirection: "column",

        scrollbarColor: `${colors.b_BrandNavy} transparent`,
        scrollbarWidth: "thin",
        maxWidth: 540,
        overflowY: { default: "auto", [xsMax]: null },
        "::-webkit-scrollbar": {
            width: "8px",
        },

        "::-webkit-scrollbar-thumb": {
            borderRadius: "6px",

            paddingBlock: "5px",
            paddingInline: "0",
            backgroundColor: colors.b_BrandNavy,
            cursor: "pointer",
        },
        "::-webkit-scrollbar-track": {
            borderRadius: "6px",
            paddingBlock: "5px",
            paddingInline: "0",
            backgroundColor: "transparent",
        },
    },

    adTag: {
        gridArea: "adTag",
        backgroundPosition: "center",
        backgroundColor: "rgba(255, 255, 255, 0.05)",
        backgroundImage: "url(https://images.goodie.pl/assets/goodie-reklama-logo.svg)",
        backgroundRepeat: "no-repeat",

        backgroundSize: "40%",

        justifySelf: "center",
        height: 250,
        marginRight: {
            default: 30,
            [xsMax]: 0,
        },
        width: 300,
    },

    iconText: {
        gap: 20,
        alignItems: "start",
        display: "inline-flex",
        flexDirection: "row",
    },

    icon: {
        height: 37,
        width: 37,
    },

    text: {
        color: colors.fg_AltDefault,
        fontWeight: 300,
        paddingTop: {
            default: null,
            ":last-child": ComposableSpacing.s1,
        },
    },

    legal: {
        gridArea: "legal",

        margin: 0,
        textAlign: "center",
        maxWidth: 540,
    },

    title: {
        gridArea: "title",
        color: colors.fg_AltDefault,
        fontWeight: 400,
        lineHeight: 1,
        textAlign: "center",
    },

    shortText: {
        gridArea: "shortText",
        color: colors.fg_AltDefault,
        fontWeight: "bold",
    },

    internalButtonsBar: {
        gridArea: "buttonsBar",
        position: { default: null, [xsMax]: "sticky" },
        bottom: { default: null, [xsMax]: 0 },
    },

    mainFullWidth: {
        gridTemplateAreas: {
            // eslint-disable-next-line quotes
            default: '"ignore title" "ignore shortText" "adTag content" "adTag legal" "adTag buttonsBar"',

            // eslint-disable-next-line quotes
            [xsMax]: `"title" "adTag" "shortText" "content" "legal" "buttonsBar"`,
        },
        backgroundColor: colors.bg_BrandPrimary,
        display: "grid",
        gridTemplateColumns: { default: "minmax(auto, 0) auto", [xsMax]: "auto" },
        gridTemplateRows: { default: "repeat(4,auto) min-content", [xsMax]: "auto auto auto auto 1fr auto" },
        justifyContent: "center",

        rowGap: { default: 35, [xsMax]: 20 },
        height: "100vh",
        paddingBottom: 10,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 10,
    },
});
